'use client';

import {
  getProformModelSources,
  isProformPreviewHandleCompatible,
} from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import type {
  ProformEdgeDescriptor,
  ProformHandleDescriptor,
  ProformModelDescriptor,
  ProformModelSource,
} from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RiRestartLine, RiZoomInLine, RiZoomOutLine } from 'react-icons/ri';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProformGlassPreviewCopy } from './proform-glass-preview-copy';
import {
  createProformGlassMaterial,
  disposeProformGlassMaterial,
  resolveProformGlassMaterial,
  updateProformGlassMaterial,
} from './proform-glass-material';
import type { ProformGlassSelection } from './proform-glass-material';
import {
  attachProformGlassBackdrop,
  createProformGlassBackdrop,
  disposeProformGlassBackdrop,
  updateProformGlassBackdrop,
} from './proform-glass-backdrop';
import type { ProformGlassBackdrop, ProformGlassBackground } from './proform-glass-backdrop';
import { createProformFillingGeometry } from './proform-filling-geometry';
import { removeProformDegenerateTriangles } from './proform-model-geometry';
import {
  disposeProformEdgeMaterials,
  prepareProformEdgeMaterials,
  setProformEdgeAppearance,
  updateProformEdgeUvs,
} from './proform-edge-material';
import type { ProformEdgeSide } from './proform-edge-material';
import { getProformEdgePreviewCopy, PROFORM_EDGE_SIDES } from './proform-edge-preview-copy';

type ThreeModule = typeof import('three');

type ModelDimensions = {
  depthMm: number;
  heightMm: number;
  widthMm: number;
};

export type ProformModelPresentation = 'front-2d' | 'interactive-3d';

export type ProformFrontFrame = {
  drawing: {
    height: number;
    left: number;
    top: number;
    width: number;
  };
  viewBoxHeight: number;
  viewBoxWidth: number;
};

type ViewerRuntime = {
  THREE: ThreeModule;
  camera: import('three').PerspectiveCamera;
  controls: import('three/examples/jsm/controls/OrbitControls.js').OrbitControls;
  createGlassEnvironment: () => import('three').WebGLRenderTarget;
  door?: import('three').Object3D;
  doorNativeSize?: import('three').Vector3;
  edgeMaterials?: ReturnType<typeof prepareProformEdgeMaterials>;
  edgeTextures?: Partial<Record<ProformEdgeSide, import('three').Texture>>;
  filling?: import('three').Mesh;
  glassMaterial?: ReturnType<typeof createProformGlassMaterial>;
  glassBackdrop?: ProformGlassBackdrop;
  glassEnvironment?: import('three').WebGLRenderTarget;
  frontCamera: import('three').OrthographicCamera;
  frontFrame?: ProformFrontFrame;
  handle?: import('three').Object3D;
  interactiveQuaternion?: import('three').Quaternion;
  interactiveViewNeedsRefit?: boolean;
  materials: import('three').MeshPhysicalMaterial[];
  model?: import('three').Object3D;
  presentation: ProformModelPresentation;
  renderer: import('three').WebGLRenderer;
  scene: import('three').Scene;
  surfaceTexture?: import('three').Texture;
};

type ProformModelViewerProps = ModelDimensions & {
  edges?: Partial<Record<ProformEdgeSide, ProformEdgeDescriptor>>;
  finishColor?: string;
  finishRotationRadians?: number;
  finishTextureUrl?: string;
  handle?: ProformHandleDescriptor;
  isActive: boolean;
  model: ProformModelDescriptor;
  presentation?: ProformModelPresentation;
  frontFrame?: ProformFrontFrame;
  glassSelection?: ProformGlassSelection;
  glassBackground?: ProformGlassBackground;
  usesFilling?: boolean;
};

type ViewerStatus =
  | { state: 'loading'; progress?: number }
  | { state: 'ready'; format: ProformModelSource['format'] }
  | { state: 'error'; message: string };

type SurfaceStatus = 'fallback' | 'loading' | 'none' | 'ready';
type HandleStatus = 'fallback' | 'loading' | 'none' | 'ready';

type HandleMountData = {
  lengthMode: ProformHandleDescriptor['lengthMode'];
  rawDepthMinMm: number;
  rawLengthCenterMm: number;
  rawLengthMm: number;
  seatYMm: number;
};

const HANDLE_MOUNT_CLEARANCE_MM = 0.15;

type PointerDragState = {
  mode: 'model' | 'pan';
  pointerId: number;
  x: number;
  y: number;
};

const FALLBACK_SURFACE_COLOR = '#b8a58e';
const MODEL_ROTATION_TRACKBALL_SENSITIVITY = 1.05;
const MODEL_ROTATION_MAX_STEP = 0.11;

const controlButtonClassName =
  'grid h-10 w-10 place-items-center rounded-full border border-border bg-background/95 text-foreground shadow-sm transition hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

function disposeObject(object: import('three').Object3D) {
  object.traverse(child => {
    const mesh = child as import('three').Mesh;
    mesh.geometry?.dispose();
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach(material => material.dispose());
    } else {
      mesh.material?.dispose();
    }
  });
}

function applyPlanarUvs(
  THREE: ThreeModule,
  object: import('three').Object3D,
  bounds: import('three').Box3,
) {
  const size = bounds.getSize(new THREE.Vector3());
  const width = Math.max(size.x, 0.001);
  const height = Math.max(size.y, 0.001);
  object.updateMatrixWorld(true);

  object.traverse(child => {
    const mesh = child as import('three').Mesh;
    if (!mesh.isMesh) return;

    const position = mesh.geometry.getAttribute('position');
    if (!position) return;

    const point = new THREE.Vector3();
    const uvs = new Float32Array(position.count * 2);
    for (let index = 0; index < position.count; index += 1) {
      point.fromBufferAttribute(position, index).applyMatrix4(mesh.matrixWorld);
      uvs[index * 2] = (point.x - bounds.min.x) / width;
      uvs[index * 2 + 1] = (point.y - bounds.min.y) / height;
    }

    mesh.geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  });
}

function updateSurfaceTextureScale(texture: import('three').Texture, dimensions: ModelDimensions) {
  texture.repeat.set(clamp(dimensions.widthMm / 600, 1, 4), clamp(dimensions.heightMm / 600, 1, 4));
  texture.needsUpdate = true;
}

function clearSurfaceTexture(runtime: ViewerRuntime, color = FALLBACK_SURFACE_COLOR) {
  runtime.materials.forEach(material => {
    material.map = null;
    material.color.set(color);
    material.needsUpdate = true;
  });
  runtime.surfaceTexture?.dispose();
  runtime.surfaceTexture = undefined;
}

function clearEdgeTextures(runtime: ViewerRuntime) {
  for (const side of PROFORM_EDGE_SIDES) {
    if (runtime.edgeMaterials) setProformEdgeAppearance(runtime.edgeMaterials, side, undefined);
    runtime.edgeTextures?.[side]?.dispose();
  }
  runtime.edgeTextures = {};
}

function ensureFillingMesh(
  runtime: ViewerRuntime,
  selection: ProformGlassSelection | undefined,
  dimensions: ModelDimensions,
) {
  if (runtime.filling) return runtime.filling;
  if (!runtime.model) return undefined;

  const { THREE } = runtime;
  runtime.glassMaterial = createProformGlassMaterial(
    THREE,
    resolveProformGlassMaterial(selection ?? {}),
    { widthMm: dimensions.widthMm * 0.72, heightMm: dimensions.heightMm * 0.92 },
    { maxAnisotropy: runtime.renderer.capabilities.getMaxAnisotropy() },
  );
  runtime.glassEnvironment ??= runtime.createGlassEnvironment();
  runtime.glassMaterial.material.envMap = runtime.glassEnvironment.texture;
  runtime.glassMaterial.material.envMapIntensity = 0.6;
  // Proform's frame OBJ files contain only the frame. The analyzed S/MF/SMF
  // models leave at least 14% at the sides and 4% at the top/bottom, so one
  // inset sheet can safely sit behind every opening without protruding when
  // the user rotates the door. Rails and mullions naturally occlude it.
  const geometry = createProformFillingGeometry(THREE, dimensions);
  const filling = new THREE.Mesh(geometry, runtime.glassMaterial.material);
  filling.name = 'proform-selected-filling';
  filling.position.z = -dimensions.depthMm * 0.42;
  // Keep real millimetre geometry outside the non-uniformly scaled OBJ root;
  // otherwise refraction thickness and the ornament pitch change with its scale.
  runtime.model.add(filling);
  runtime.filling = filling;
  return filling;
}

function removeFillingMesh(runtime: ViewerRuntime) {
  if (!runtime.filling) return;
  runtime.filling.removeFromParent();
  runtime.filling.geometry.dispose();
  if (runtime.glassMaterial) disposeProformGlassMaterial(runtime.glassMaterial);
  runtime.filling = undefined;
  runtime.glassMaterial = undefined;
}

function removeGlassBackdrop(runtime: ViewerRuntime) {
  if (runtime.glassBackdrop) disposeProformGlassBackdrop(runtime.glassBackdrop);
  runtime.glassBackdrop = undefined;
}

function renderViewer(runtime: ViewerRuntime) {
  const camera = getRenderCamera(runtime);
  if (runtime.glassBackdrop) updateProformGlassBackdrop(runtime.glassBackdrop, camera);
  runtime.renderer.render(runtime.scene, camera);
}

function bindPointerControls(runtime: ViewerRuntime) {
  const { THREE, camera, controls, renderer } = runtime;
  const canvas = renderer.domElement;
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let drag: PointerDragState | null = null;

  const pickModel = (clientX: number, clientY: number) => {
    if (runtime.presentation !== 'interactive-3d' || !runtime.model) return false;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(pointer, camera);
    return raycaster.intersectObject(runtime.model, true).length > 0;
  };

  const projectToTrackball = (clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect();
    const radius = Math.max(Math.min(rect.width, rect.height) * 0.5, 1);
    const x = (clientX - rect.left - rect.width * 0.5) / radius;
    const y = (rect.height * 0.5 - (clientY - rect.top)) / radius;
    const distance = Math.hypot(x, y);
    const z = distance < Math.SQRT1_2 ? Math.sqrt(1 - distance * distance) : 0.5 / distance;
    return new THREE.Vector3(x, y, z).normalize();
  };

  const rotateModel = (fromX: number, fromY: number, toX: number, toY: number) => {
    const model = runtime.model;
    if (!model || (Math.abs(toX - fromX) < 0.01 && Math.abs(toY - fromY) < 0.01)) return;

    const from = projectToTrackball(fromX, fromY);
    const to = projectToTrackball(toX, toY);
    const axis = new THREE.Vector3().crossVectors(from, to);
    if (axis.lengthSq() < 0.000001) return;

    const angle = clamp(
      Math.atan2(axis.length(), clamp(from.dot(to), -1, 1)) * MODEL_ROTATION_TRACKBALL_SENSITIVITY,
      -MODEL_ROTATION_MAX_STEP,
      MODEL_ROTATION_MAX_STEP,
    );
    camera.updateMatrixWorld();
    axis.normalize().transformDirection(camera.matrixWorld);

    const rotation = new THREE.Quaternion().setFromAxisAngle(axis, angle);
    model.quaternion.copy(rotation.multiply(model.quaternion.clone()).normalize());
    model.updateMatrixWorld(true);
  };

  const updateCursor = (clientX: number, clientY: number) => {
    if (runtime.presentation !== 'interactive-3d') {
      canvas.style.cursor = 'default';
      return;
    }
    canvas.style.cursor = pickModel(clientX, clientY) ? 'grab' : 'move';
  };

  const onPointerDown = (event: PointerEvent) => {
    if (
      runtime.presentation !== 'interactive-3d' ||
      !event.isPrimary ||
      event.button !== 0 ||
      !runtime.model
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    drag = {
      mode: pickModel(event.clientX, event.clientY) ? 'model' : 'pan',
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    canvas.setPointerCapture?.(event.pointerId);
    canvas.style.cursor = 'grabbing';
  };

  const onPointerMove = (event: PointerEvent) => {
    if (runtime.presentation !== 'interactive-3d') {
      drag = null;
      canvas.style.cursor = 'default';
      return;
    }
    if (!drag) {
      updateCursor(event.clientX, event.clientY);
      return;
    }
    if (event.pointerId !== drag.pointerId) return;

    event.preventDefault();
    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;

    if (drag.mode === 'model') {
      rotateModel(drag.x, drag.y, event.clientX, event.clientY);
    } else {
      controls.pan(deltaX, deltaY);
      controls.update();
    }

    drag.x = event.clientX;
    drag.y = event.clientY;
  };

  const endDrag = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const pointerId = drag.pointerId;
    drag = null;
    if (canvas.hasPointerCapture?.(pointerId)) canvas.releasePointerCapture?.(pointerId);
    updateCursor(event.clientX, event.clientY);
  };

  const onPointerLeave = () => {
    if (!drag) canvas.style.cursor = 'default';
  };
  const onContextMenu = (event: MouseEvent) => {
    if (runtime.presentation === 'interactive-3d') event.preventDefault();
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
  canvas.addEventListener('lostpointercapture', endDrag);
  canvas.addEventListener('pointerleave', onPointerLeave);
  canvas.addEventListener('contextmenu', onContextMenu);

  return () => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', endDrag);
    canvas.removeEventListener('pointercancel', endDrag);
    canvas.removeEventListener('lostpointercapture', endDrag);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('contextmenu', onContextMenu);
  };
}

async function loadModelSource(
  THREE: ThreeModule,
  source: ProformModelSource,
  onProgress: (progress: number) => void,
) {
  const load = <T,>(loader: {
    load: (
      url: string,
      onLoad: (object: T) => void,
      onProgress?: (event: ProgressEvent<EventTarget>) => void,
      onError?: (error: unknown) => void,
    ) => void;
  }) =>
    new Promise<T>((resolve, reject) => {
      loader.load(
        source.url,
        resolve,
        event => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
        reject,
      );
    });

  if (source.format === 'obj') {
    const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
    return load(new OBJLoader());
  }

  const { TDSLoader } = await import('three/examples/jsm/loaders/TDSLoader.js');
  const object = await load(new TDSLoader());
  // 3DS stores the same supplied geometry with Y/Z axes swapped relative to OBJ.
  object.rotation.x = -Math.PI / 2;
  object.updateMatrixWorld(true);
  return object;
}

async function loadModelWithFallback(
  THREE: ThreeModule,
  sources: ProformModelSource[],
  onProgress: (progress: number) => void,
) {
  let lastError: unknown;

  for (const source of sources) {
    try {
      const object = await loadModelSource(THREE, source, onProgress);
      object.updateMatrixWorld(true);

      let hasRenderableGeometry = false;
      object.traverse(child => {
        const mesh = child as import('three').Mesh;
        const positions = mesh.isMesh ? mesh.geometry.getAttribute('position') : null;
        if (positions && positions.count >= 3) hasRenderableGeometry = true;
      });
      const bounds = new THREE.Box3().setFromObject(object);
      const size = bounds.getSize(new THREE.Vector3());
      const hasFiniteBounds =
        !bounds.isEmpty() &&
        [size.x, size.y, size.z].every(value => Number.isFinite(value)) &&
        size.lengthSq() > 0.000001;

      if (!hasRenderableGeometry || !hasFiniteBounds) {
        disposeObject(object);
        throw new Error(`3D source ${source.url} neobsahuje platnou geometrii.`);
      }

      return {
        format: source.format,
        object,
      };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('3D model se nepodařilo načíst.');
}

async function loadHandleObject(THREE: ThreeModule, handle: ProformHandleDescriptor) {
  const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
  const geometry = await new STLLoader().loadAsync(handle.source.url);
  geometry.computeBoundingBox();
  geometry.computeVertexNormals();

  const bounds = geometry.boundingBox;
  if (!bounds) {
    geometry.dispose();
    throw new Error('STL úchytky nemá platnou geometrii.');
  }

  const size = bounds.getSize(new THREE.Vector3());
  const expected = handle.nativeSizeMm;
  const dimensionsAreValid =
    Math.abs(size.x - expected.profileDepth) < 1 &&
    Math.abs(size.y - expected.profileHeight) < 1 &&
    Math.abs(size.z - expected.length) < 1 &&
    Number.isFinite(handle.mountingSeatYMm) &&
    handle.mountingSeatYMm >= bounds.min.y &&
    handle.mountingSeatYMm <= bounds.max.y;
  if (!dimensionsAreValid) {
    geometry.dispose();
    throw new Error('Rozměry STL úchytky neodpovídají konfiguraci.');
  }

  const material = new THREE.MeshPhysicalMaterial({
    clearcoat: 0.35,
    clearcoatRoughness: 0.32,
    color: handle.color,
    metalness: 0.82,
    roughness: 0.28,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = handle.id;
  mesh.userData.proformHandleMount = {
    lengthMode: handle.lengthMode,
    rawDepthMinMm: bounds.min.x,
    rawLengthCenterMm: (bounds.min.z + bounds.max.z) / 2,
    rawLengthMm: size.z,
    seatYMm: handle.mountingSeatYMm,
  } satisfies HandleMountData;
  // The raw STL origin is the mounting reference. Native Z runs along the
  // handle, X goes from the back of the door towards the front grip and
  // negative Y seats the tongue down into the top edge. The descriptor's
  // seat plane keeps the metal cap above the door while the routed tongue
  // remains embedded in the panel.
  mesh.rotation.set(0, -Math.PI / 2, 0);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function fitCamera(runtime: ViewerRuntime | null, preset: 'front' | 'perspective' | 'preserve') {
  if (!runtime?.model) return;
  const { THREE, camera, controls, model, renderer } = runtime;
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const radius = Math.max(size.length() / 2, 1);
  const verticalFov = (camera.fov * Math.PI) / 180;
  const aspect = Math.max(renderer.domElement.clientWidth / renderer.domElement.clientHeight, 0.2);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
  // Fitting the bounding sphere against the narrower FOV is deliberately a
  // little more conservative than fitting width/height alone. It keeps the
  // whole door visible even in a narrow sidebar and in an angled perspective.
  const limitingHalfFov = Math.max(Math.min(verticalFov, horizontalFov) / 2, 0.05);
  const fitDistance = (radius / Math.sin(limitingHalfFov)) * 1.18;

  const currentDirection = camera.position.clone().sub(controls.target).normalize();
  const direction =
    preset === 'front'
      ? new THREE.Vector3(0, 0, 1)
      : preset === 'perspective'
        ? new THREE.Vector3(0.62, 0.28, 1).normalize()
        : currentDirection.lengthSq() > 0
          ? currentDirection
          : new THREE.Vector3(0.62, 0.28, 1).normalize();

  controls.target.copy(center);
  camera.position.copy(center).addScaledVector(direction, fitDistance);
  camera.near = Math.max(fitDistance / 1000, 0.01);
  camera.far = fitDistance * 20;
  camera.updateProjectionMatrix();
  controls.minDistance = Math.max(radius * 0.3, 1);
  controls.maxDistance = Math.max(radius * 12, fitDistance * 2);
  controls.update();
}

function fitFrontCamera(runtime: ViewerRuntime | null) {
  if (!runtime?.model || !runtime.door) return;
  const { THREE, door, frontCamera, frontFrame, model, renderer } = runtime;
  model.updateMatrixWorld(true);

  // Dimensions belong to the door panel, not to accessories. In particular, a
  // handle above the top edge must not shrink or shift the dimensioned panel.
  const doorBox = new THREE.Box3().setFromObject(door);
  const doorSize = doorBox.getSize(new THREE.Vector3());
  const doorCenter = doorBox.getCenter(new THREE.Vector3());
  const viewportWidth = Math.max(renderer.domElement.clientWidth, 1);
  const viewportHeight = Math.max(renderer.domElement.clientHeight, 1);

  let frameLeft = viewportWidth * 0.16;
  let frameTop = viewportHeight * 0.16;
  let frameWidth = viewportWidth * 0.68;
  let frameHeight = viewportHeight * 0.68;

  if (frontFrame) {
    const logicalScale = Math.min(
      viewportWidth / Math.max(frontFrame.viewBoxWidth, 1),
      viewportHeight / Math.max(frontFrame.viewBoxHeight, 1),
    );
    const offsetX = (viewportWidth - frontFrame.viewBoxWidth * logicalScale) / 2;
    const offsetY = (viewportHeight - frontFrame.viewBoxHeight * logicalScale) / 2;
    frameLeft = offsetX + frontFrame.drawing.left * logicalScale;
    frameTop = offsetY + frontFrame.drawing.top * logicalScale;
    frameWidth = frontFrame.drawing.width * logicalScale;
    frameHeight = frontFrame.drawing.height * logicalScale;
  }

  frameWidth = Math.max(frameWidth, 1);
  frameHeight = Math.max(frameHeight, 1);
  const worldPerPixel = Math.max(doorSize.x / frameWidth, doorSize.y / frameHeight, 0.000001);
  const viewWidth = viewportWidth * worldPerPixel;
  const viewHeight = viewportHeight * worldPerPixel;
  const worldLeft = doorBox.min.x - frameLeft * worldPerPixel;
  const worldTop = doorBox.max.y + frameTop * worldPerPixel;
  const cameraCenterX = worldLeft + viewWidth / 2;
  const cameraCenterY = worldTop - viewHeight / 2;

  const fullBox = new THREE.Box3().setFromObject(model);
  const fullSize = fullBox.getSize(new THREE.Vector3());
  const distance = Math.max(Math.max(doorSize.x, doorSize.y) * 2, fullSize.z * 4, 100);

  frontCamera.left = -viewWidth / 2;
  frontCamera.right = viewWidth / 2;
  frontCamera.top = viewHeight / 2;
  frontCamera.bottom = -viewHeight / 2;
  frontCamera.near = 0.1;
  frontCamera.far = distance + fullSize.z * 4 + 1000;
  frontCamera.position.set(cameraCenterX, cameraCenterY, fullBox.max.z + distance);
  frontCamera.up.set(0, 1, 0);
  frontCamera.lookAt(cameraCenterX, cameraCenterY, doorCenter.z);
  frontCamera.updateProjectionMatrix();
  frontCamera.updateMatrixWorld(true);
}

function getRenderCamera(runtime: ViewerRuntime) {
  return runtime.presentation === 'front-2d' ? runtime.frontCamera : runtime.camera;
}

function placeHandle(runtime: ViewerRuntime, dimensions: ModelDimensions) {
  if (!runtime.handle) return;
  const mount = runtime.handle.userData.proformHandleMount as HandleMountData | undefined;
  if (!mount) return;

  const targetLengthMm = mount.lengthMode === 'fit-edge' ? dimensions.widthMm : mount.rawLengthMm;
  const lengthScale = targetLengthMm / Math.max(mount.rawLengthMm, 0.001);

  // Ry(-PI/2) maps the STL's native Z length onto the door's X axis.
  // Scaling local Z therefore fits the handle to the configured width while
  // leaving the native 37 x 11 mm X/Y cross-section unchanged.
  runtime.handle.scale.set(1, 1, lengthScale);

  runtime.handle.position.set(
    mount.rawLengthCenterMm * lengthScale,
    dimensions.heightMm / 2 + HANDLE_MOUNT_CLEARANCE_MM - mount.seatYMm,
    -dimensions.depthMm / 2 - mount.rawDepthMinMm,
  );
}

function applyDimensions(runtime: ViewerRuntime, dimensions: ModelDimensions) {
  if (!runtime.model || !runtime.door || !runtime.doorNativeSize) return;
  const { door, doorNativeSize, model } = runtime;
  door.scale.set(
    dimensions.widthMm / Math.max(doorNativeSize.x, 0.001),
    dimensions.heightMm / Math.max(doorNativeSize.y, 0.001),
    dimensions.depthMm / Math.max(doorNativeSize.z, 0.001),
  );
  placeHandle(runtime, dimensions);
  if (runtime.edgeMaterials) updateProformEdgeUvs(runtime.edgeMaterials, dimensions);
  if (runtime.filling && runtime.glassMaterial) {
    runtime.filling.geometry.dispose();
    runtime.filling.geometry = createProformFillingGeometry(runtime.THREE, dimensions);
    runtime.filling.position.z = -dimensions.depthMm * 0.42;
    updateProformGlassMaterial(runtime.glassMaterial, runtime.glassMaterial.descriptor, {
      widthMm: dimensions.widthMm * 0.72,
      heightMm: dimensions.heightMm * 0.92,
    });
  }
  model.updateMatrixWorld(true);
  if (runtime.presentation === 'front-2d') {
    runtime.interactiveViewNeedsRefit = true;
    fitFrontCamera(runtime);
  } else {
    fitCamera(runtime, 'preserve');
  }
}

export function ProformModelViewer({
  depthMm,
  edges,
  finishColor,
  finishRotationRadians = 0,
  finishTextureUrl,
  frontFrame,
  glassSelection,
  glassBackground = 'none',
  handle,
  heightMm,
  isActive,
  model,
  presentation = 'interactive-3d',
  usesFilling = false,
  widthMm,
}: ProformModelViewerProps) {
  const locale = useLocaleStore(state => state.currentLocale);
  const compatibleHandle =
    handle && isProformPreviewHandleCompatible(handle, widthMm) ? handle : undefined;
  const mountRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<ViewerRuntime | null>(null);
  const dimensionsRef = useRef<ModelDimensions>({ depthMm, heightMm, widthMm });
  const edgesRef = useRef(edges);
  const edgeTextureRequestRef = useRef(0);
  const edgePreviewEnabled = edges !== undefined;
  const edgeSelectionSignature = JSON.stringify(
    PROFORM_EDGE_SIDES.map(side => [
      edges?.[side]?.code,
      edges?.[side]?.color,
      edges?.[side]?.textureUrl,
    ]),
  );
  const finishColorRef = useRef(finishColor);
  const finishRotationRadiansRef = useRef(finishRotationRadians);
  const finishTextureUrlRef = useRef(finishTextureUrl);
  const frontFrameRef = useRef(frontFrame);
  const usesFillingRef = useRef(usesFilling);
  const glassSelectionRef = useRef(glassSelection);
  const glassBackgroundRef = useRef(glassBackground);
  const backdropRequestRef = useRef(0);
  const handleRef = useRef(compatibleHandle);
  const handleRequestRef = useRef(0);
  const textureRequestRef = useRef(0);
  const activeRef = useRef(isActive);
  const presentationRef = useRef(presentation);
  const [status, setStatus] = useState<ViewerStatus>({ state: 'loading' });
  const [surfaceStatus, setSurfaceStatus] = useState<SurfaceStatus>(
    finishTextureUrl ? 'loading' : finishColor ? 'ready' : 'none',
  );
  const [handleStatus, setHandleStatus] = useState<HandleStatus>(
    compatibleHandle ? 'loading' : 'none',
  );
  const [backdropStatus, setBackdropStatus] = useState<SurfaceStatus>('none');
  const [edgeStatuses, setEdgeStatuses] = useState<Partial<Record<ProformEdgeSide, SurfaceStatus>>>(
    {},
  );

  dimensionsRef.current = { depthMm, heightMm, widthMm };
  edgesRef.current = edges;
  finishColorRef.current = finishColor;
  finishRotationRadiansRef.current = finishRotationRadians;
  finishTextureUrlRef.current = finishTextureUrl;
  frontFrameRef.current = frontFrame;
  usesFillingRef.current = usesFilling;
  glassSelectionRef.current = glassSelection;
  glassBackgroundRef.current = glassBackground;
  handleRef.current = compatibleHandle;
  activeRef.current = isActive;
  presentationRef.current = presentation;
  const frontFrameSignature = frontFrame
    ? [
        frontFrame.drawing.height,
        frontFrame.drawing.left,
        frontFrame.drawing.top,
        frontFrame.drawing.width,
        frontFrame.viewBoxHeight,
        frontFrame.viewBoxWidth,
      ].join(':')
    : '';

  const updateHandle = useCallback(
    async (runtime: ViewerRuntime, nextHandle: ProformHandleDescriptor | undefined) => {
      const request = handleRequestRef.current + 1;
      handleRequestRef.current = request;

      if (runtime.handle) {
        runtime.model?.remove(runtime.handle);
        disposeObject(runtime.handle);
        runtime.handle = undefined;
      }

      if (!nextHandle) {
        setHandleStatus('none');
        applyDimensions(runtime, dimensionsRef.current);
        return;
      }

      setHandleStatus('loading');
      try {
        const loadedHandle = await loadHandleObject(runtime.THREE, nextHandle);
        if (handleRequestRef.current !== request || runtimeRef.current !== runtime) {
          disposeObject(loadedHandle);
          return;
        }

        runtime.handle = loadedHandle;
        runtime.model?.add(loadedHandle);
        applyDimensions(runtime, dimensionsRef.current);
        setHandleStatus('ready');
      } catch {
        if (handleRequestRef.current === request && runtimeRef.current === runtime) {
          setHandleStatus('fallback');
          applyDimensions(runtime, dimensionsRef.current);
        }
      }
    },
    [],
  );

  const updateSurfaceTexture = useCallback(
    async (
      runtime: ViewerRuntime,
      surface: { color?: string; rotationRadians: number; textureUrl?: string },
    ) => {
      const request = textureRequestRef.current + 1;
      textureRequestRef.current = request;
      clearSurfaceTexture(runtime, surface.color);

      if (!surface.textureUrl) {
        setSurfaceStatus(surface.color ? 'ready' : 'none');
        return;
      }

      setSurfaceStatus('loading');
      try {
        const texture = await new runtime.THREE.TextureLoader().loadAsync(surface.textureUrl);
        if (textureRequestRef.current !== request || runtimeRef.current !== runtime) {
          texture.dispose();
          return;
        }

        texture.colorSpace = runtime.THREE.SRGBColorSpace;
        // The supplied finish previews are not seamless tiles. Mirroring keeps
        // their physical scale without exposing a bright line at every repeat.
        texture.wrapS = runtime.THREE.MirroredRepeatWrapping;
        texture.wrapT = runtime.THREE.MirroredRepeatWrapping;
        texture.center.set(0.5, 0.5);
        texture.rotation = surface.rotationRadians;
        texture.anisotropy = runtime.renderer.capabilities.getMaxAnisotropy();
        updateSurfaceTextureScale(texture, dimensionsRef.current);

        runtime.surfaceTexture = texture;
        runtime.materials.forEach(material => {
          material.map = texture;
          material.color.set('#ffffff');
          material.needsUpdate = true;
        });
        setSurfaceStatus('ready');
      } catch {
        if (textureRequestRef.current === request && runtimeRef.current === runtime) {
          setSurfaceStatus('fallback');
        }
      }
    },
    [],
  );

  const updateEdges = useCallback(async (runtime: ViewerRuntime) => {
    const request = ++edgeTextureRequestRef.current;
    clearEdgeTextures(runtime);
    const selectedEdges = edgesRef.current;
    setEdgeStatuses(
      Object.fromEntries(
        PROFORM_EDGE_SIDES.map(side => [side, selectedEdges?.[side] ? 'loading' : 'none']),
      ),
    );

    await Promise.all(
      PROFORM_EDGE_SIDES.map(async side => {
        const edge = selectedEdges?.[side];
        if (!edge) return;
        const isCurrent = () =>
          request === edgeTextureRequestRef.current && runtimeRef.current === runtime;
        const setSideStatus = (status: SurfaceStatus) => {
          if (isCurrent()) setEdgeStatuses(previous => ({ ...previous, [side]: status }));
        };
        if (
          !runtime.edgeMaterials ||
          runtime.edgeMaterials.edgeMaterials[side].length === 0 ||
          (!edge.textureUrl && !edge.color)
        ) {
          setSideStatus('fallback');
          return;
        }
        if (!edge.textureUrl) {
          setProformEdgeAppearance(runtime.edgeMaterials, side, { color: edge.color });
          setSideStatus('ready');
          return;
        }
        try {
          const texture = await new runtime.THREE.TextureLoader().loadAsync(edge.textureUrl);
          if (!isCurrent()) {
            texture.dispose();
            return;
          }
          texture.colorSpace = runtime.THREE.SRGBColorSpace;
          texture.wrapS = runtime.THREE.MirroredRepeatWrapping;
          texture.wrapT = runtime.THREE.MirroredRepeatWrapping;
          texture.anisotropy = runtime.renderer.capabilities.getMaxAnisotropy();
          // Edge sample strips are not square. Preserve their image proportions;
          // actual-mm UVs are maintained independently for each side of the door.
          const image = texture.image as { width?: number; height?: number };
          if (image.width && image.height) texture.repeat.y = image.width / image.height;
          runtime.edgeTextures ??= {};
          runtime.edgeTextures[side] = texture;
          setProformEdgeAppearance(runtime.edgeMaterials, side, { texture });
          setSideStatus('ready');
        } catch {
          setSideStatus('fallback');
        }
      }),
    );
  }, []);

  const updateFilling = useCallback((runtime: ViewerRuntime, enabled: boolean) => {
    if (!enabled) {
      removeFillingMesh(runtime);
      return;
    }

    ensureFillingMesh(runtime, glassSelectionRef.current, dimensionsRef.current);
    if (runtime.glassMaterial) {
      updateProformGlassMaterial(
        runtime.glassMaterial,
        resolveProformGlassMaterial(glassSelectionRef.current ?? {}),
        {
          widthMm: dimensionsRef.current.widthMm * 0.72,
          heightMm: dimensionsRef.current.heightMm * 0.92,
        },
        { maxAnisotropy: runtime.renderer.capabilities.getMaxAnisotropy() },
      );
    }
  }, []);

  const updateBackdrop = useCallback(async (runtime: ViewerRuntime) => {
    const request = ++backdropRequestRef.current;
    removeGlassBackdrop(runtime);
    const kind = glassBackgroundRef.current;
    // The background belongs to the scene, not to a selected pane. It also
    // provides a before/after comparison through an as-yet unfilled frame.
    if (kind === 'none') {
      setBackdropStatus('none');
      return;
    }
    setBackdropStatus('loading');
    try {
      const backdrop = await createProformGlassBackdrop(
        runtime.THREE,
        kind,
        getRenderCamera(runtime),
      );
      if (request !== backdropRequestRef.current || runtimeRef.current !== runtime) {
        disposeProformGlassBackdrop(backdrop);
        return;
      }
      updateProformGlassBackdrop(backdrop, getRenderCamera(runtime));
      // Scene backgrounds are drawn before the opaque and transmission passes,
      // without entering model bounds or pointer hit testing.
      attachProformGlassBackdrop(backdrop, runtime.scene);
      runtime.glassBackdrop = backdrop;
      setBackdropStatus('ready');
    } catch {
      if (request === backdropRequestRef.current && runtimeRef.current === runtime) {
        setBackdropStatus('fallback');
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let animationFrame = 0;
    let cleanup = () => {};

    async function setup() {
      const mount = mountRef.current;
      if (!mount) return;
      setStatus({ state: 'loading' });

      try {
        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
        const { RoomEnvironment } =
          await import('three/examples/jsm/environments/RoomEnvironment.js');
        if (cancelled) return;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 10000);
        const frontCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10000);
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setClearColor(0xffffff, 0);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.08;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mount.replaceChildren(renderer.domElement);

        scene.add(new THREE.HemisphereLight('#ffffff', '#a9a39a', 2.15));
        const keyLight = new THREE.DirectionalLight('#fffdf8', 3.2);
        keyLight.position.set(-500, 700, 900);
        scene.add(keyLight);
        const fillLight = new THREE.DirectionalLight('#d9e4ff', 1.3);
        fillLight.position.set(800, -300, 500);
        scene.add(fillLight);
        const rimLight = new THREE.DirectionalLight('#fff2dc', 1.15);
        rimLight.position.set(-700, 50, -500);
        scene.add(rimLight);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.enablePan = true;
        controls.screenSpacePanning = true;
        controls.zoomToCursor = true;
        controls.mouseButtons.LEFT = null;
        controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
        controls.mouseButtons.RIGHT = null;
        controls.touches.ONE = null;
        controls.touches.TWO = null;
        controls.enabled = presentationRef.current === 'interactive-3d';

        const runtime: ViewerRuntime = {
          THREE,
          camera,
          controls,
          createGlassEnvironment: () => {
            const room = new RoomEnvironment();
            const generator = new THREE.PMREMGenerator(renderer);
            try {
              return generator.fromScene(room, 0.04);
            } finally {
              room.dispose();
              generator.dispose();
            }
          },
          frontCamera,
          frontFrame: frontFrameRef.current,
          materials: [],
          presentation: presentationRef.current,
          renderer,
          scene,
        };
        runtimeRef.current = runtime;
        let unbindPointerControls = () => {};

        const resize = () => {
          const width = Math.max(mount.clientWidth, 1);
          const height = Math.max(mount.clientHeight, 1);
          renderer.setSize(width, height);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          if (runtime.model) {
            if (runtime.presentation === 'front-2d') {
              fitFrontCamera(runtime);
            } else {
              fitCamera(runtime, 'preserve');
            }
          }
        };
        const observer = new ResizeObserver(resize);
        observer.observe(mount);
        resize();

        cleanup = () => {
          window.cancelAnimationFrame(animationFrame);
          observer.disconnect();
          unbindPointerControls();
          controls.dispose();
          handleRequestRef.current += 1;
          textureRequestRef.current += 1;
          edgeTextureRequestRef.current += 1;
          backdropRequestRef.current += 1;
          runtime.surfaceTexture?.dispose();
          clearEdgeTextures(runtime);
          if (runtime.edgeMaterials) disposeProformEdgeMaterials(runtime.edgeMaterials);
          removeFillingMesh(runtime);
          removeGlassBackdrop(runtime);
          runtime.glassEnvironment?.dispose();
          if (runtime.model) disposeObject(runtime.model);
          renderer.dispose();
          renderer.domElement.remove();
        };

        const loaded = await loadModelWithFallback(
          THREE,
          getProformModelSources(model),
          progress => {
            if (!cancelled) setStatus({ state: 'loading', progress });
          },
        );
        if (cancelled) {
          disposeObject(loaded.object);
          return;
        }

        const sourceBox = new THREE.Box3().setFromObject(loaded.object);
        const sourceCenter = sourceBox.getCenter(new THREE.Vector3());
        const nativeSize = sourceBox.getSize(new THREE.Vector3());
        loaded.object.position.sub(sourceCenter);
        loaded.object.updateMatrixWorld(true);
        const centeredBounds = new THREE.Box3().setFromObject(loaded.object);
        applyPlanarUvs(THREE, loaded.object, centeredBounds);

        const previewMaterial = new THREE.MeshPhysicalMaterial({
          clearcoat: 0.12,
          clearcoatRoughness: 0.7,
          color: FALLBACK_SURFACE_COLOR,
          metalness: 0,
          roughness: 0.5,
          side: THREE.DoubleSide,
        });

        loaded.object.traverse(child => {
          const mesh = child as import('three').Mesh;
          if (!mesh.isMesh) return;
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach(material => material.dispose());
          } else {
            mesh.material?.dispose();
          }
          removeProformDegenerateTriangles(mesh.geometry);
          mesh.geometry.computeVertexNormals();
          const material = previewMaterial.clone();
          mesh.material = material;
          runtime.materials.push(material);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
        });
        previewMaterial.dispose();

        if (edgePreviewEnabled) {
          runtime.edgeMaterials = prepareProformEdgeMaterials(
            THREE,
            loaded.object,
            centeredBounds,
            {
              outerRadiusMm: model.edgeRadiusMm,
            },
          );
        }

        const doorScaleRoot = new THREE.Group();
        doorScaleRoot.name = `${model.id}-door`;
        doorScaleRoot.add(loaded.object);
        const pivot = new THREE.Group();
        pivot.name = `${model.id}-pivot`;
        pivot.add(doorScaleRoot);
        runtime.door = doorScaleRoot;
        runtime.doorNativeSize = nativeSize;
        runtime.model = pivot;
        scene.add(pivot);
        applyDimensions(runtime, dimensionsRef.current);
        if (runtime.presentation === 'front-2d') {
          runtime.model.quaternion.identity();
          fitFrontCamera(runtime);
        } else {
          // Start in a true front view so the model silhouette has the same
          // physical width/height ratio as the dimensioned 2D drawing. The
          // perspective preset and drag rotation remain available in controls.
          fitCamera(runtime, 'front');
        }
        unbindPointerControls = bindPointerControls(runtime);
        void updateHandle(runtime, handleRef.current);
        void updateEdges(runtime);
        updateFilling(runtime, usesFillingRef.current);
        void updateBackdrop(runtime);
        void updateSurfaceTexture(runtime, {
          color: finishColorRef.current,
          rotationRadians: finishRotationRadiansRef.current,
          textureUrl: finishTextureUrlRef.current,
        });
        setStatus({ state: 'ready', format: loaded.format });

        const render = () => {
          animationFrame = window.requestAnimationFrame(render);
          if (!activeRef.current) return;
          if (runtime.presentation === 'interactive-3d') controls.update();
          renderViewer(runtime);
        };
        render();
      } catch (error) {
        if (!cancelled) {
          setStatus({
            state: 'error',
            message: error instanceof Error ? error.message : '3D model se nepodařilo načíst.',
          });
        }
      }
    }

    setup();

    return () => {
      cancelled = true;
      cleanup();
      runtimeRef.current = null;
    };
  }, [
    model,
    edgePreviewEnabled,
    updateBackdrop,
    updateEdges,
    updateFilling,
    updateHandle,
    updateSurfaceTexture,
  ]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model) void updateEdges(runtime);
  }, [edgeSelectionSignature, updateEdges]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model && runtime.door) void updateHandle(runtime, compatibleHandle);
  }, [compatibleHandle, updateHandle]);

  useLayoutEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    const previousPresentation = runtime.presentation;
    runtime.presentation = presentation;
    runtime.controls.enabled = presentation === 'interactive-3d';
    if (!runtime.model) return;

    if (presentation === 'front-2d') {
      if (previousPresentation === 'interactive-3d') {
        runtime.interactiveQuaternion = runtime.model.quaternion.clone();
        runtime.interactiveViewNeedsRefit = false;
      }
      runtime.model.quaternion.identity();
      runtime.model.updateMatrixWorld(true);
      fitFrontCamera(runtime);
    } else {
      if (runtime.interactiveQuaternion) {
        runtime.model.quaternion.copy(runtime.interactiveQuaternion);
        runtime.interactiveQuaternion = undefined;
        runtime.model.updateMatrixWorld(true);
      }
      if (runtime.interactiveViewNeedsRefit) {
        fitCamera(runtime, 'preserve');
      } else {
        runtime.controls.update();
      }
      runtime.interactiveViewNeedsRefit = false;
    }

    if (activeRef.current) {
      renderViewer(runtime);
    }
  }, [presentation]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.frontFrame = frontFrameRef.current;
    if (runtime.model && runtime.presentation === 'front-2d') {
      fitFrontCamera(runtime);
      if (activeRef.current) renderViewer(runtime);
    }
  }, [frontFrameSignature]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model) {
      const dimensions = { depthMm, heightMm, widthMm };
      applyDimensions(runtime, dimensions);
      if (runtime.surfaceTexture) updateSurfaceTextureScale(runtime.surfaceTexture, dimensions);
    }
  }, [depthMm, heightMm, widthMm]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model) {
      void updateSurfaceTexture(runtime, {
        color: finishColor,
        rotationRadians: finishRotationRadians,
        textureUrl: finishTextureUrl,
      });
    }
  }, [finishColor, finishRotationRadians, finishTextureUrl, updateSurfaceTexture]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model) {
      updateFilling(runtime, usesFilling);
    }
  }, [
    updateFilling,
    usesFilling,
    glassSelection?.groupCode,
    glassSelection?.fillingCode,
    glassSelection?.orientation,
    glassSelection?.ornamentSide,
    glassSelection?.color,
  ]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime?.model) void updateBackdrop(runtime);
  }, [glassBackground, updateBackdrop]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || !isActive) return;
    const frame = window.requestAnimationFrame(() => {
      const mount = mountRef.current;
      if (!mount) return;
      runtime.renderer.setSize(Math.max(mount.clientWidth, 1), Math.max(mount.clientHeight, 1));
      runtime.camera.aspect = Math.max(mount.clientWidth / mount.clientHeight, 0.2);
      runtime.camera.updateProjectionMatrix();
      if (runtime.model) {
        if (runtime.presentation === 'front-2d') {
          fitFrontCamera(runtime);
        } else {
          fitCamera(runtime, 'preserve');
        }
      }
      renderViewer(runtime);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isActive]);

  const zoom = (factor: number) => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const offset = runtime.camera.position
      .clone()
      .sub(runtime.controls.target)
      .multiplyScalar(factor);
    runtime.camera.position.copy(runtime.controls.target).add(offset);
    runtime.controls.update();
  };

  const resetView = () => {
    const runtime = runtimeRef.current;
    if (!runtime?.model) return;
    runtime.model.quaternion.identity();
    runtime.model.updateMatrixWorld(true);
    fitCamera(runtime, 'perspective');
  };

  const isFrontPresentation = presentation === 'front-2d';

  return (
    <div
      className={
        isFrontPresentation
          ? 'relative h-full min-h-[360px] w-full overflow-hidden bg-[#f7f7f5]'
          : 'relative h-full min-h-[360px] w-full overflow-hidden bg-[#f5f3ef]'
      }
      data-presentation={presentation}
      data-finish-texture-state={surfaceStatus}
      data-edge-top-state={edgeStatuses.top ?? 'none'}
      data-edge-right-state={edgeStatuses.right ?? 'none'}
      data-edge-bottom-state={edgeStatuses.bottom ?? 'none'}
      data-edge-left-state={edgeStatuses.left ?? 'none'}
      data-handle-model-state={handleStatus}
      data-glass-material={
        usesFilling ? resolveProformGlassMaterial(glassSelection ?? {}).id : 'none'
      }
      data-glass-background={glassBackground}
      data-glass-background-state={backdropStatus}
      style={{
        backgroundImage: isFrontPresentation
          ? 'linear-gradient(rgba(31,41,55,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(31,41,55,0.05) 1px, transparent 1px)'
          : 'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.96) 0%, rgba(245,243,239,0.9) 52%, rgba(226,221,214,0.92) 100%)',
        backgroundSize: isFrontPresentation ? '24px 24px' : undefined,
      }}
    >
      <div
        ref={mountRef}
        className={
          isFrontPresentation
            ? 'pointer-events-none h-full min-h-[360px] w-full'
            : 'h-full min-h-[360px] w-full touch-none'
        }
        role={isFrontPresentation ? undefined : 'application'}
        aria-hidden={isFrontPresentation || undefined}
        aria-label={
          isFrontPresentation
            ? undefined
            : `Interaktivní 3D model ${model.label}. Tažením po produktu jej otočíte, tažením po pozadí posunete a kolečkem přiblížíte.`
        }
      />

      {status.state === 'loading' && (
        <div className="absolute inset-0 grid place-items-center bg-background/80 px-8 text-center backdrop-blur-sm">
          <div>
            <span className="mx-auto mb-3 block h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-accent" />
            <p className="text-sm font-semibold">
              {isFrontPresentation ? 'Načítám čelní 2D pohled...' : 'Načítám 3D model...'}
            </p>
            {status.progress !== undefined && (
              <p className="mt-1 text-xs text-muted-foreground">{status.progress} %</p>
            )}
          </div>
        </div>
      )}

      {status.state === 'error' && (
        <div
          className={`absolute inset-0 grid place-items-center p-8 text-center ${
            model.sources.length === 0 ? 'bg-muted' : 'bg-[#FFEBEB]'
          }`}
        >
          <div>
            <p
              className={
                model.sources.length === 0 ? 'font-semibold' : 'font-semibold text-destructive'
              }
            >
              {model.sources.length === 0
                ? `${isFrontPresentation ? '2D náhled' : '3D model'} pro toto provedení zatím není dostupný`
                : `${isFrontPresentation ? '2D náhled' : '3D model'} se nepodařilo načíst`}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {model.sources.length === 0
                ? 'Na serveru chybí nebo nelze načíst stejně pojmenovaný soubor .obj.'
                : status.message}
            </p>
          </div>
        </div>
      )}

      {!isFrontPresentation && status.state === 'ready' && (
        <div
          className="absolute bottom-4 right-4 flex flex-col gap-2"
          role="group"
          aria-label="Ovládání 3D pohledu"
        >
          <button
            type="button"
            className={controlButtonClassName}
            onClick={() => zoom(0.82)}
            aria-label="Přiblížit"
            title="Přiblížit"
          >
            <RiZoomInLine size={18} />
          </button>
          <button
            type="button"
            className={controlButtonClassName}
            onClick={() => zoom(1.22)}
            aria-label="Oddálit"
            title="Oddálit"
          >
            <RiZoomOutLine size={18} />
          </button>
          <button
            type="button"
            className={controlButtonClassName}
            onClick={resetView}
            aria-label="Obnovit pohled"
            title="Obnovit pohled"
          >
            <RiRestartLine size={18} />
          </button>
        </div>
      )}

      {!isFrontPresentation && surfaceStatus === 'fallback' && (
        <div className="pointer-events-none absolute left-4 top-12 rounded-full border border-amber-300 bg-amber-50/95 px-3 py-1.5 text-[11px] font-semibold text-amber-900 shadow-sm">
          Vzorek povrchu se nepodařilo načíst
        </div>
      )}

      {PROFORM_EDGE_SIDES.some(side => edgeStatuses[side] === 'fallback') && (
        <div
          role="status"
          className="pointer-events-none absolute bottom-3 left-3 right-16 rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-xs text-amber-900 shadow-sm"
        >
          {getProformEdgePreviewCopy(locale).unavailable}:{' '}
          {PROFORM_EDGE_SIDES.filter(side => edgeStatuses[side] === 'fallback')
            .map(side => getProformEdgePreviewCopy(locale).sides[side])
            .join(', ')}
        </div>
      )}

      {backdropStatus === 'fallback' && (
        <div
          role="status"
          className="pointer-events-none absolute left-4 top-4 rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-xs text-amber-900 shadow-sm"
        >
          {getProformGlassPreviewCopy(locale).backgroundError}
        </div>
      )}

      {!isFrontPresentation && handleStatus === 'fallback' && (
        <div className="pointer-events-none absolute left-4 top-28 rounded-full border border-amber-300 bg-amber-50/95 px-3 py-1.5 text-[11px] font-semibold text-amber-900 shadow-sm">
          Model úchytky se nepodařilo načíst
        </div>
      )}
    </div>
  );
}
